export const copyTabs = (tabsMacros) => {
  const copy = tabsMacros.map((t) => {
    const tab = { ...t };

    tab.macros = t.macros.map((m) => {
      const macros = { ...m };

      macros.toggles.map((toggle) => {
        if (typeof toggle === 'object') return { ...toggle };

        return toggle;
      });

      return macros;
    });

    return tab;
  });

  return copy;
};

/**
 * the POST/PUT request body requires the toggle value to
 * be a key value pair
 */
export const togglesObj = (value) => {
  let obj = {};

  // convert value to an obj
  value.forEach((v) => (obj = { ...obj, ...v }));

  return obj;
};

/**
 * @param [num]
 * crudTotal is the sum of the numbers in a crud array
 * [0, 1, 2] === 3. If the value is greater then 0 one
 * of the crud values has been set and the parent switch
 * will be on.
 */
export const crudTotal = (togglesArr) => {
  return togglesArr.reduce((init, val) => {
    return init + val;
  }, 0);
};

const crudObjTotal = (crudObj) => {
  return Object.keys(crudObj).reduce((init, key) => {
    return init + crudObj[key];
  }, 0);
};

/**
 * @param string(['None', 'Owner Only', 'Everything'])
 * @param number(number([0, 1, 2])
 * @param undefined
 * convertCrud takes a single crud value and converts it
 * to its counter part.
 */
export const convertCrud = (crudValue) => {
  let value = null;

  switch (true) {
    case crudValue === undefined:
      value = 'None';
      break;
    case Object.prototype.toString.call(crudValue) === '[object String]': {
      if (crudValue === 'None') value = 0;
      if (crudValue === 'Owner Only') value = 1;
      if (crudValue === 'Everything') value = 2;
      break;
    }
    case typeof crudValue === 'number': {
      if (crudValue === 0) value = 'None';
      if (crudValue === 1) value = 'Owner Only';
      if (crudValue === 2) value = 'Everything';
      break;
    }
    default:
    // TODO: throw an Error
  }

  return value;
};

/**
 * Add parent property to roles
 */
export const getPermissionsHelper = (payload) => {
  if (!payload || !Array.isArray(payload.tabs)) {
    return [];
  }
  const tabs = payload.tabs.map((tab) => {
    const t = { ...tab };
    const macros = t.macros.map((macro) => {
      const m = { ...macro };

      if (m.type === 'crud') {
        m.parent = true;
        m.toggles = [2, 2, 2];
      }

      if (m.type === 'toggle') {
        m.parent = false;
        m.toggles = m.toggles.map((toggle) => {
          return { [toggle]: false };
        });
      }

      return m;
    });

    t.macros = macros;

    return t;
  });

  return tabs;
};

export const customPermissionsHelper = (permissions) => {
  return permissions.map((permission) => {
    const p = { ...permission };

    p.parent = true;
    p.toggles = [2, 2, 2];

    return p;
  });
};

/**
 * Add parent property to roles if "crud" convert
 * roles[].value._crud to roles[].value = [_crud] and
 * roles[]value.(!_crud) to roles[].value = [{toggles: value}...]
 */
export const getRolesHelper = (payload) => {
  const roles = payload.map((r) => {
    const role = { ...r };
    const { toggles, value } = role;

    const [type] = Object.keys(value ?? {});

    /**
     * If role is a type "crud"
     */
    if (type === '_crud') {
      if (crudTotal(value._crud) > 0) {
        role.parent = true;
      } else if (crudTotal(value._crud) === 0) {
        role.parent = false;
      }
      role.value = value._crud;

      return role;
    }

    if (role.crud) {
      if (crudObjTotal(role.crud) > 0) {
        role.parent = true;
      } else if (crudObjTotal(role.crud) === 0) {
        role.parent = false;
      }

      role.value = role.crud;
      return role;
    }

    /**
     * If role is a type "toggles"
     */
    const togglesTypes = Object.keys(value ?? toggles ?? {});
    // find out if one of the toggles values is set to true
    const parent = togglesTypes.some(
      (t) => (value?.[t] ?? toggles?.[t]) === true,
    );

    if (parent) {
      role.parent = true;
    } else if (!parent) {
      role.parent = false;
    }
    // convert toggle value to an array of objects
    role.value = togglesTypes.map((t) => ({ [t]: value?.[t] ?? toggles?.[t] }));

    return role;
  });

  return roles;
};

/**
 * Take roles which have been processed with the getRolesHelper
 * and add them to tabsMacros so that settings are reflected
 * in the tabsMacros
 */
// TODO: instead of calling getRolesHelper, updateTabsMacros and copyTabs
// at different times set them up so they can be piped functions...
export const updateTabsMacros = (roles, tabsMacros, customPermissions) => {
  const copyTabMacros = copyTabs(tabsMacros);

  /**
   * If customPermissions are not passed
   * they will not be added to the form
   */
  if (customPermissions) {
    const custom = customPermissionsHelper(customPermissions);

    copyTabMacros[0].macros = [...copyTabMacros[0].macros, ...custom];
  }

  roles.forEach((role) => {
    // find the tab the role is in
    for (const tab of copyTabMacros) {
      const macro = tab.macros.find(
        (m) => m.id === role.resource_id || m.id === role.resourceId,
      );

      if (macro) {
        if (macro.type === 'crud') macro.toggles = role.value;
        if (macro.type === 'toggle') {
          const roleToggles = role.value.map((t) => {
            const [toggle] = Object.keys(t);

            return toggle;
          });
          const macroToggles = [];

          macro.toggles.forEach((t) => {
            const [toggle] = Object.keys(t);

            const index = roleToggles.findIndex((roleToggle) => {
              return roleToggle === toggle;
            });

            if (index >= 0) {
              macroToggles.push(role.value[index]);
            } else {
              macroToggles.push({ [toggle]: false });
            }
          });

          macro.toggles = macroToggles;
        }
        macro.parent = role.parent;
        break; // if role is found start over
      }
    }
  });

  return copyTabMacros;
};

/**
 * get the transformed values from macro
 * for roleRequestBody
 */
export const roleSettings = (tabs) => {
  const settings = tabs.reduce((tState, tab) => {
    const macros = tab.macros.reduce((mState, m) => {
      if (m.type === 'crud') {
        const macro = {
          resource_id: m.id,
          value: m.toggles,
        };

        return [...mState, macro];
      }
      if (m.type === 'toggle') {
        const macro = {
          resource_id: m.id,
          value: togglesObj(m.toggles),
        };

        return [...mState, macro];
      }

      return mState;
    }, []);

    return [...tState, ...macros];
  }, []);

  return settings;
};
